import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormGroupDirective, Validators } from "@angular/forms";
import { map } from "rxjs/operators";
import { SubSink } from "subsink";
import { AutomapperService } from "../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../core/environment.tokens";
import { FormService } from "../../dynamic-forms/form.service";
import { Autocomplete } from "../../dynamic-forms/inputs/autocomplete/autocomplete.model";
import { CheckboxGroup } from "../../dynamic-forms/inputs/checkbox-group/checkbox-group.model";
import { DynamicInput } from "../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../dynamic-forms/inputs/selectable-input.model";
import { Textbox } from "../../dynamic-forms/inputs/textbox/textbox.model";
import { GapQueryRouteFilters } from "../../platform/modules/project/gap-query/qap-query-route-filters.model";
import { ProjectService } from "../../platform/modules/project/project.service";
import { ArrayHelper } from "../../utilities/contracts/array-helper";
import { DateHelper } from "../../utilities/contracts/date-helper";
import { DateFormats } from "../../utilities/contracts/helper-types";
import { StringHelper } from "../../utilities/contracts/string-helper";
import { BulkAction } from "../grid/bulk-actions/bulk-action.model";
import { GridView } from "../grid/grid-menu/grid-views/grid-view.model";
import { GridViewsState } from "../grid/grid-menu/grid-views/grid-views-state.model";
import { GridViewsService } from "../grid/grid-menu/grid-views/grid-views.service";
import { GridPipeName } from "../grid/grid-pipe.enum";
import { GridStateService } from "../grid/grid-state.service";
import { GridColumnDefinition } from "../grid/models/grid-column-definition.model";
import { GridConfiguration } from "../grid/models/grid-configuration.model";
import { GridFilter } from "../grid/models/grid-filter.model";
import { GridRequest } from "../grid/models/grid-request.model";
import { ServerGridComponent } from "../grid/server-grid/server-grid.component";
import { MemberIdComponent } from "./member-id.component";

@Component({
  selector: "app-member-grid",
  templateUrl: "./member-grid.component.html",
  styleUrls: ["./member-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberGridComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  @ViewChild(ServerGridComponent, { static: true }) serverGridComponent: ServerGridComponent;
  @ViewChild("memberIdColumn", { static: true }) memberIdColumn: TemplateRef<MemberIdComponent>;
  @ViewChild("memberForm") memberForm: FormGroupDirective;
  @Input() stateName = "";
  @Input() url = `${this.baseApiUrl}member/query`;
  @Input() additionalFilters: GridFilter[] = [];
  @Input() additionalColumns: GridColumnDefinition[] = [];
  @Input() additionalBulkActions: BulkAction[] = [];
  @Input() sortDirection = 1;
  @Input() sortField = "";
  @Input() masterMemberIdRouteUrl = "/chartlake/members/:masterMemberId";
  @Input() viewAttributeId = 0;
  @Input() selectionMode = "none";
  @Input() showViews = false;
  @Input() showActionColumn = false;
  @Input() selection: any[];
  @Input() routeParameters: GapQueryRouteFilters;
  @Input() selectProjectTab = false;
  @Output() selectionChange = new EventEmitter<any>();
  refreshGrid = new EventEmitter<GridConfiguration>(true);
  refreshViews = new EventEmitter<GridView>(true);
  configuration: GridConfiguration;
  request: GridRequest;
  views: GridViewsState;
  actions: BulkAction[];
  isMemberQuery = true;
  onlyChartsInput = false;
  filterHeaderText = "Chart Lake";

  get projectsInput(): CheckboxGroup {
    return this.getInput("Projects");
  }
  set projectsInput(value: CheckboxGroup) {
    this.setInput("Projects", value);
  }

  get memberFirstNameInput(): Textbox {
    return this.getInput("MemberFirstName");
  }

  get memberLastNameInput(): Textbox {
    return this.getInput("MemberLastName");
  }

  get memberDobInput(): Textbox {
    return this.getInput("MemberDateOfBirth");
  }

  get reveleerMemberIndexInput(): Textbox {
    return this.getInput("MasterMemberId");
  }

  get memberIdInput(): Textbox {
    return this.getInput("MemberId");
  }

  get memberKeyInput(): Textbox {
    return this.getInput("SourceAliasId");
  }

  get memberStatusInput(): Autocomplete {
    return this.getInput("DataSet");
  }
  set memberStatusInput(value: Autocomplete) {
    this.setInput("DataSet", value);
  }

  get isGapFilterValid(): boolean {
    return ArrayHelper.isAvailable(this.memberForm?.value?.Projects);
  }

  get filterFooterText(): string {
    let text = "";

    if (this.memberForm?.value?.MemberFirstName) {
      if (!this.isMemberFilterValid) {
        text = "Please enter additional Member information";
      }
    }

    return text;
  }

  get isMemberFilterValid(): boolean {
    if (this.memberForm?.value) {
      this.onlyChartsInput = this.memberForm.value.MemberWithCharts === "1";
      const { DataSet, MemberFirstName, MemberWithCharts, ...filteredMemberForm } = this.memberForm.value;

      if (this.isObjectNull(filteredMemberForm)) {
        return false;
      }
    }

    return true;
  }

  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private readonly automapper: AutomapperService,
    private readonly projectService: ProjectService,
    private readonly gridStateService: GridStateService,
    private readonly formService: FormService,
    private gridViewsService: GridViewsService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.getAllSelectableInputs();
    this.createGrid();
    this.showFiltersOrGetData();
    this.sink.add(
      this.refreshViews.subscribe(gridView => this.getViews(gridView))
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  getInput<T extends DynamicInput>(key: string): T {
    if (this.request == null) {
      return null;
    }

    return this.request.getInput<T>(key);
  }

  setInput<T extends DynamicInput>(key: string, value: T): void {
    if (this.request == null) {
      return null;
    }

    this.request.setInput<T>(key, value);
  }

  formatMemberDobDate(): void {
    const dobFilter = this.request.getFilter("MemberDateOfBirth");
    const formattedValue = DateHelper.format(dobFilter.value);
    dobFilter.value = formattedValue;
    this.formService.updateDom.next();
  }

  updateGridConfiguration(alreadyInitialized = true) {
    this.configuration = new GridConfiguration({
      isInit: alreadyInitialized,
      stateName: this.stateName,
      columns: this.getColumns(),
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100],
      selectionMode: this.selectionMode,
      showViews: this.showViews,
      viewAttributeId: this.viewAttributeId,
      showActionColumn: this.showActionColumn,
    });
  }

  createGrid(): void {
    this.updateGridConfiguration(false);

    this.request = new GridRequest({
      url: this.url,
      sortDirection: this.sortDirection,
      sortField: this.sortField,
      filters: this.getFilters(),
    });

    this.getViews();
    this.actions = this.getActions();
  }

  private getColumns(): GridColumnDefinition[] {
    const defaultColumns = [
      new GridColumnDefinition({ field: "masterMemberId", template: this.memberIdColumn, header: "Member Index" }),
      new GridColumnDefinition({ field: "memberFirstName", header: "First Name" }),
      new GridColumnDefinition({ field: "memberLastName", header: "Last Name" }),
      new GridColumnDefinition({ field: "memberDateOfBirth", header: "DOB", pipeName: GridPipeName.Date, format: DateFormats.GRID_DATE_FORMAT }),
      new GridColumnDefinition({ field: "memberGender", header: "Gender" }),
      new GridColumnDefinition({ field: "dataSet", header: "Status", show: false }),
    ];

    this.additionalColumns.forEach(a => {
      const matchedIndex = defaultColumns.findIndex(b => b.field === a.field);
      if (matchedIndex < 0) {
        defaultColumns.push(a);
      } else {
        defaultColumns.splice(matchedIndex, 1, a);
      }
    });

    return defaultColumns;
  }

  private getFilters(): GridFilter[] {
    const memberValidator = [
      Validators.minLength(2),
      Validators.maxLength(100),
    ];
    const memberErrorMessage = {
      minlength: `Minimum 2 characters and Maximum 100 characters allowed`,
      maxlength: `Minimum 2 characters and Maximum 100 characters allowed`,
    };

    const totalFilters = [
      ...this.additionalFilters,
      new GridFilter({
        input: new CheckboxGroup(),
        key: "Projects",
        name: "Projects",
        value: this.getRouteParamterValue("projectIds").toString(),
        forceFilter: true,
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberFirstName",
        name: "First Name",
        validators: memberValidator,
        errorMessages: memberErrorMessage,
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberLastName",
        name: "Last Name",
        validators: memberValidator,
        errorMessages: memberErrorMessage,
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberDateOfBirth",
        name: "DOB",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MasterMemberId",
        name: "Reveleer Member Index",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberId",
        name: "Member Id",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "SourceAliasId",
        name: "Member Key",
      }),
      new GridFilter({
        input: new Autocomplete({
          placeholder: "Active",
          options: [
            new SelectableInput({ text: "Active", value: "Active" }),
            new SelectableInput({ text: "Inactive", value: "Inactive" }),
            new SelectableInput({ text: "Archived", value: "Archived" }),
            new SelectableInput({ text: "All", value: "All" }),
          ],
        }),
        key: "DataSet",
        name: "Project Status",
        value: "Active",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "MemberWithCharts",
        name: "Charts Only",
        show: false,
      }),
    ];

    const distinctFilters = totalFilters.filter((filter, i, filters) => {
      return filters.findIndex(a => a.key === filter.key) === i;
    });
    return distinctFilters;
  }

  get showMemberWithCharts(): string {
    return this.memberForm.control.get("MemberWithCharts").value;
  }

  get hasRouteParamaters(): boolean {
    if (this.routeParameters === undefined) {
      return false;
    }
    const parametersWithValues = Object.values(this.routeParameters).filter(x => StringHelper.isAvailable(x));
    return ArrayHelper.isAvailable(parametersWithValues);
  }

  private getRouteParamterValue(name: string): string {
    return this.hasRouteParamaters ? this.routeParameters[name] : "";
  }

  private getViews(gridView: GridView | null = null): void {
    if (this.configuration.showViews) {
      this.gridViewsService.get(this.configuration.viewAttributeId).subscribe(views => {
        this.views = views;
        this.configuration.showGridHeader = true;

        if (gridView != null) {
          setTimeout(() => this.serverGridComponent.onViewSelect.emit(gridView));
        }

        this.changeDetector.markForCheck();
      });
    }
  }

  memberChartEmit(event: boolean) {
    const control = this.memberForm.control.get("MemberWithCharts");
    if (event) {
      control.setValue("1");
    } else {
      control.setValue(null);
    }
  }

  private getActions(): BulkAction[] {
    const totalBulkActions = [
      ...this.additionalBulkActions,
    ];

    const distinctBulkActions = totalBulkActions.filter((filter, i, filters) => {
      return filters.findIndex(a => a.name === filter.name) === i;
    });

    return distinctBulkActions;
  }

  showFiltersOrGetData(): void {
    let filters = [];

    if (StringHelper.isAvailable(this.stateName) && this.gridStateService.hasKey(this.stateName)) {
      const gridState = this.gridStateService.get(this.stateName);
      filters = gridState.request.filters.filter(filter => filter.input.value);
    } else {
      filters = this.request.filters.filter(filter => StringHelper.isAvailable(filter.input.value));
    }

    if (filters.length > 0) {
      setTimeout(() => this.refreshGrid.emit());
    }

    const isFilterHasValue = this.serverGridComponent.isFilterHasValue(filters);

    if (history.state.isNavigationLink || !isFilterHasValue) {
      this.serverGridComponent.showFilters();
    }
  }

  getAllSelectableInputs(): void {
    this.getProjects();
  }

  private getProjects(): void {
    this.projectService.getProjectListAll()
      .pipe(map(this.automapper.curryMany("LookupModel", "SelectableInput")))
      .subscribe(options => {
        this.projectsInput = { ...this.projectsInput, options } as any;
        this.setProjectsInput();
        this.setControlValue(this.projectsInput, false);
        this.formService.updateDom.next();
      });
  }

  private setProjectsInput(): void {
    const projectFilter = this.request.getFilter(this.projectsInput.key);
    if (StringHelper.isAvailable(projectFilter.value)) {
      const optionsValue = this.projectsInput.options.find(a => a.value === +projectFilter.value);
      projectFilter.inputValue = [optionsValue];
    }
  }

  private setControlValue(control: CheckboxGroup, isStringValue = true): void {
    const filter = this.request.getFilter(control.key);
    if (StringHelper.isAvailable(filter.input.value)) {
      const filterData = filter.input.value.split(",");
      filterData.forEach(item => {
        if (ArrayHelper.isAvailable(control.options)) {
          filter.inputValue.push(control.options.find(a => a.value === (isStringValue ? item : +item)));
        }
      });
      control.selectedOptions = filter.inputValue;
    }
  }

  private isObjectNull(obj: object): boolean {
    for (const key in obj) {
      if (obj[key] != null && obj[key] !== "") {
        return false;
      }
    }

    return true;
  }

  trackByIndex(index, item) {
    return index;
  }
}
